// TEMPORARILY DISABLED (TDEV-150): email/password login relies on hard-coded
// mock credentials on the backend, so it is trivial to bypass. The email input
// is rendered but disabled, and the password/verification steps are commented
// out below until a real credential store lands; only Google SSO works.
// PasswordLoginForm / EmailVerificationForm imports are dropped while disabled.
import { ENTRA_SAML_FIREBASE_PROVIDER_ID, type TenantSignInProvider } from '@dawn/feature-config';
import { LoginForm, SocialLoginButton } from '@dawn/ui';
import { GoogleAuthProvider, SAMLAuthProvider, signInWithPopup } from 'firebase/auth';
import { useState, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { authApi } from '@/lib/api';
import { getFirebaseAuth } from '@/lib/firebase';
import { SESSION_CHANNEL_NAME } from '@/providers/SessionProvider';
import { useSessionStore } from '@/stores/session';
import { useTenantStore } from '@/stores/tenant';

interface LocationState {
  from?: { pathname: string; search?: string };
  fromInvite?: boolean;
}

/** Provider id of the tenant-level SAML config minted for Entra tenants (TDEV-156). */
const ENTRA_REJECTED_MESSAGE = 'Microsoft rejected the sign-in. Contact your administrator.';

/**
 * Which Identity Platform provider the popup drives. Both paths end in the same
 * ID token → `POST /auth/identity-platform` exchange; only the popup differs.
 */
function buildAuthProvider(
  signInProvider: TenantSignInProvider
): GoogleAuthProvider | SAMLAuthProvider {
  return signInProvider === 'entra-saml'
    ? new SAMLAuthProvider(ENTRA_SAML_FIREBASE_PROVIDER_ID)
    : new GoogleAuthProvider();
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { status, setActive } = useSessionStore();
  const { company } = useTenantStore();

  // Error state (shared with the SSO flow)
  const [error, setError] = useState<string | null>(null);

  // SSO popup state
  const [ssoLoading, setSsoLoading] = useState(false);

  // Tenants provisioned before TDEV-156 have no provider on the wire: Google.
  const signInProvider: TenantSignInProvider = company?.signInProvider ?? 'google';

  const locationState = location.state as LocationState | undefined;
  // Keep the query: the MCP OAuth consent page (TDEV-1199) carries the whole
  // authorization request in it.
  const from = locationState?.from
    ? `${locationState.from.pathname}${locationState.from.search ?? ''}`
    : '/';

  // --- Redirect when session is active ---
  useEffect(() => {
    if (status === 'active') {
      const returnUrl = sessionStorage.getItem('returnUrl');
      sessionStorage.removeItem('returnUrl');
      navigate(returnUrl || from, { replace: true });
    }
  }, [status, from, navigate]);

  // SSO login via Identity Platform — Google for Dawn-hosted tenants, the
  // tenant's federated SAML provider for Entra tenants (TDEV-156).
  const handleSsoLogin = useCallback(async () => {
    setSsoLoading(true);
    setError(null);

    try {
      const auth = getFirebaseAuth();

      // Scope Firebase Auth to the Identity Platform tenant
      const tenantId = company?.identityPlatformTenantId;
      if (tenantId) {
        auth.tenantId = tenantId;
      }

      const result = await signInWithPopup(auth, buildAuthProvider(signInProvider));
      const idToken = await result.user.getIdToken();

      const response = await authApi.identityPlatformLogin(idToken);
      setActive(response.user, response.sessionCreatedAt, response.maxAllowedTime);
      const loginChannel = new BroadcastChannel(SESSION_CHANNEL_NAME);
      loginChannel.postMessage({ type: 'login' });
      loginChannel.close();

      // Sign out of Firebase — we use our own sessions
      await auth.signOut();

      const returnUrl = sessionStorage.getItem('returnUrl');
      sessionStorage.removeItem('returnUrl');
      navigate(returnUrl || from, { replace: true });
    } catch (err) {
      const firebaseError = err as { code?: string; response?: { data?: { message?: string } } };

      // Silently handle popup closed by user
      if (firebaseError.code === 'auth/popup-closed-by-user') {
        return;
      }

      // The API rejected a verified token (no invitation, suspended, …): its
      // message is the copy, whichever provider issued the token.
      const apiMessage = firebaseError.response?.data?.message;
      if (apiMessage) {
        setError(apiMessage);
        return;
      }

      if (signInProvider === 'entra-saml') {
        // Any Firebase-side failure means the IdP or Identity Platform rejected
        // the assertion — the fix is on the customer's side, not a retry.
        setError(
          firebaseError.code?.startsWith('auth/')
            ? ENTRA_REJECTED_MESSAGE
            : 'Failed to sign in. Please try again.'
        );
        return;
      }

      // User tried to sign in but hasn't been invited (disabledUserSignup is enabled on tenant)
      if (firebaseError.code === 'auth/admin-restricted-operation') {
        setError('No invitation found for this email. Contact your administrator.');
        return;
      }

      setError('Failed to sign in with Google. Please try again.');
    } finally {
      setSsoLoading(false);
    }
  }, [company, signInProvider, from, navigate, setActive]);

  // ==================== DISABLED: email/password login (TDEV-150) ====================
  // The multi-step email -> password -> verification flow below is disabled because
  // the backend validates against hard-coded mock credentials, making it trivial to
  // bypass. It is kept here (commented out) so it can be restored once a real
  // credential store is in place. To re-enable: uncomment this block, restore the
  // LoginForm/PasswordLoginForm/EmailVerificationForm imports and the step/email/
  // loading/verificationId/maskedEmail state, and render renderStep() instead of the
  // Google-only card below.
  //
  // // Step 1: Email submission
  // const handleEmailSubmit = useCallback((submittedEmail: string) => {
  //   setEmail(submittedEmail);
  //   setError(null);
  //   setStep('password');
  // }, []);
  //
  // // Step 2: Password submission
  // const handlePasswordSubmit = useCallback(
  //   async (password: string) => {
  //     setLoading(true);
  //     setError(null);
  //     try {
  //       const response = await authApi.login(email, password);
  //       setVerificationId(response.verificationId);
  //       setMaskedEmail(response.maskedEmail);
  //       setStep('verification');
  //     } catch (err) {
  //       const errorResponse = (err as { response?: { data?: { message?: string; code?: string } } })
  //         ?.response?.data;
  //       if (errorResponse?.code === 'USER_NOT_FOUND') {
  //         setStep('email');
  //         setError('No account found with this email address');
  //       } else {
  //         setError(errorResponse?.message || 'Invalid email or password');
  //       }
  //     } finally {
  //       setLoading(false);
  //     }
  //   },
  //   [email]
  // );
  //
  // // Step 2: Back to email
  // const handleBackToEmail = useCallback(() => {
  //   setError(null);
  //   setStep('email');
  // }, []);
  //
  // // Step 3: Verification code submission
  // const handleVerificationSubmit = useCallback(
  //   async (code: string) => {
  //     if (!verificationId) {
  //       setError('Invalid verification session');
  //       return;
  //     }
  //     setLoading(true);
  //     setError(null);
  //     try {
  //       const response = await authApi.verifyCode(verificationId, code);
  //       setActive(response.user, response.sessionCreatedAt, response.maxAllowedTime);
  //       const verifyChannel = new BroadcastChannel(SESSION_CHANNEL_NAME);
  //       verifyChannel.postMessage({ type: 'login' });
  //       verifyChannel.close();
  //       const returnUrl = sessionStorage.getItem('returnUrl');
  //       sessionStorage.removeItem('returnUrl');
  //       navigate(returnUrl || from, { replace: true });
  //     } catch (err) {
  //       const message =
  //         (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
  //         'Invalid verification code';
  //       setError(message);
  //     } finally {
  //       setLoading(false);
  //     }
  //   },
  //   [verificationId, from, navigate, setActive]
  // );
  //
  // // Step 3: Resend code
  // const handleResendCode = useCallback(async () => {
  //   if (!verificationId) {
  //     setError('Invalid verification session');
  //     return;
  //   }
  //   setLoading(true);
  //   setError(null);
  //   try {
  //     await authApi.resendCode(verificationId);
  //   } catch (err) {
  //     const message =
  //       (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
  //       'Failed to resend code';
  //     setError(message);
  //   } finally {
  //     setLoading(false);
  //   }
  // }, [verificationId]);
  //
  // // Step 3: Back to password
  // const handleBackToPassword = useCallback(() => {
  //   setError(null);
  //   setStep('password');
  // }, []);
  //
  // // Render step content
  // const renderStep = () => {
  //   switch (step) {
  //     case 'email':
  //       return (
  //         <LoginForm
  //           onEmailSubmit={handleEmailSubmit}
  //           loading={loading || ssoLoading}
  //           error={error ?? undefined}
  //           socialProviders={[]}
  //           customSocialContent={
  //             <SocialLoginButton
  //               provider="google"
  //               onClick={handleSsoLogin}
  //               loading={ssoLoading}
  //               className="w-[322px]"
  //             />
  //           }
  //         />
  //       );
  //     case 'password':
  //       return (
  //         <PasswordLoginForm
  //           email={email}
  //           onSubmit={handlePasswordSubmit}
  //           onBack={handleBackToEmail}
  //           loading={loading}
  //           error={error ?? undefined}
  //         />
  //       );
  //     case 'verification':
  //       return (
  //         <EmailVerificationForm
  //           onSubmit={handleVerificationSubmit}
  //           onResendCode={handleResendCode}
  //           onBack={handleBackToPassword}
  //           loading={loading}
  //           error={error ?? undefined}
  //           description={`Enter the 4-digit verification code that has been sent to ${maskedEmail}`}
  //         />
  //       );
  //   }
  // };
  // ==================== END DISABLED ====================

  // Email input rendered for visual parity but disabled (TDEV-150) — only the
  // tenant's SSO button is functional while email/password login is blocked.
  // Drop the `disabled` prop when the flow is re-enabled.
  return (
    <LoginForm
      disabled
      error={error ?? undefined}
      socialProviders={[]}
      customSocialContent={
        <SocialLoginButton
          provider={signInProvider === 'entra-saml' ? 'microsoft' : 'google'}
          onClick={handleSsoLogin}
          loading={ssoLoading}
          className="w-[322px]"
        />
      }
    />
  );
}
